/**
 * Human-readable presentation for agent tool calls.
 *
 * A messenger should show *that* OpenClaw did something and roughly what, not a
 * JSON dump. Titles and detail keys follow the canonical upstream map
 * (`apps/shared/OpenClawKit/.../tool-display.json`) so the wording matches what
 * the user sees in OpenClaw's own surfaces. They stay English in
 * every interface language: a tool name is the handle a person searches for.
 */

export type ToolDisplaySpec = {
  emoji: string;
  title: string;
  /** Argument fields, in priority order, worth showing next to the title. */
  detailKeys?: string[];
};

const FALLBACK_DETAIL_KEYS = [
  "command",
  "path",
  "url",
  "targetUrl",
  "ref",
  "name",
  "query",
  "pattern",
  "action",
];

const TOOL_DISPLAY: Record<string, ToolDisplaySpec> = {
  bash: { emoji: "🛠️", title: "Ran a command", detailKeys: ["command"] },
  exec: { emoji: "🛠️", title: "Ran a command", detailKeys: ["command"] },
  terminal: { emoji: "⌨️", title: "Terminal", detailKeys: ["action", "command", "cwd"] },
  process: { emoji: "🧰", title: "Process", detailKeys: ["sessionId"] },
  read: { emoji: "📖", title: "Read a file", detailKeys: ["path"] },
  write: { emoji: "✍️", title: "Wrote a file", detailKeys: ["path"] },
  edit: { emoji: "📝", title: "Edited a file", detailKeys: ["path"] },
  apply_patch: { emoji: "🩹", title: "Applied a patch" },
  attach: { emoji: "📎", title: "Attachment", detailKeys: ["path", "url", "fileName"] },
  api: { emoji: "🌐", title: "API call", detailKeys: ["url", "endpoint", "method"] },
  browser: { emoji: "🌐", title: "Browser", detailKeys: ["action", "url"] },
  portal: { emoji: "🌐", title: "Portal", detailKeys: ["action", "port", "title"] },
  canvas: { emoji: "🖼️", title: "Canvas" },
  computer: { emoji: "🖱️", title: "Computer", detailKeys: ["action", "text"] },
  mobile_ui: { emoji: "📱", title: "Mobile UI", detailKeys: ["action"] },
  screen: { emoji: "🖥️", title: "Screen", detailKeys: ["action"] },
  dashboard: { emoji: "📋", title: "Dashboard", detailKeys: ["action", "title"] },
  nodes: { emoji: "📱", title: "Devices" },
  cron: { emoji: "⏰", title: "Schedule" },
  web_search: { emoji: "🔎", title: "Web search", detailKeys: ["query"] },
  web_fetch: { emoji: "📄", title: "Fetched a page", detailKeys: ["url"] },
  memory_search: { emoji: "🧠", title: "Searched memory", detailKeys: ["query"] },
  memory_get: { emoji: "📓", title: "Read memory", detailKeys: ["path"] },
  code_execution: { emoji: "🧮", title: "Ran code", detailKeys: ["task"] },
  image: { emoji: "🖼️", title: "Image", detailKeys: ["path", "url", "prompt"] },
  view_image: { emoji: "🖼️", title: "Viewed an image", detailKeys: ["path", "url"] },
  image_generate: { emoji: "🎨", title: "Generated an image", detailKeys: ["prompt"] },
  music_generate: { emoji: "🎵", title: "Generated music" },
  ask_user: { emoji: "❓", title: "Asked a question", detailKeys: ["questions.0.question"] },
  progress_card: { emoji: "🗺️", title: "Progress" },
  get_goal: { emoji: "🎯", title: "Read the goal" },
  create_goal: { emoji: "🎯", title: "Created a goal", detailKeys: ["objective"] },
  update_goal: { emoji: "🎯", title: "Updated the goal", detailKeys: ["status"] },
  suggest_task: { emoji: "✨", title: "Suggested a task", detailKeys: ["title"] },
  dismiss_task: { emoji: "🗑️", title: "Dismissed a task", detailKeys: ["reason"] },
  skill_workshop: { emoji: "🧰", title: "Skill workshop", detailKeys: ["action", "name"] },
  openclaw: { emoji: "🦀", title: "OpenClaw", detailKeys: ["action", "path"] },
  gateway: { emoji: "🔌", title: "Gateway", detailKeys: ["action", "path"] },
  sessions: { emoji: "🗂️", title: "Chat settings" },
  sessions_list: { emoji: "🗂️", title: "Listed chats", detailKeys: ["label", "search"] },
  sessions_send: { emoji: "📨", title: "Sent a message", detailKeys: ["label"] },
  sessions_history: { emoji: "🧾", title: "Read the history", detailKeys: ["sessionKey"] },
  sessions_search: { emoji: "🔎", title: "Searched chats", detailKeys: ["query"] },
  sessions_spawn: { emoji: "🧑‍🔧", title: "Started a sub-agent", detailKeys: ["label", "task"] },
  subagents: { emoji: "🤖", title: "Sub-agents" },
  agents_list: { emoji: "🧭", title: "Listed agents" },
  agents_wait: { emoji: "⏳", title: "Waited for agents" },
  conversations_list: { emoji: "💬", title: "Conversations", detailKeys: ["channel"] },
  conversations_send: { emoji: "📨", title: "Sent a message" },
  conversations_turn: { emoji: "↔️", title: "Conversation" },
  transcripts: { emoji: "🎙️", title: "Transcripts" },
  message: { emoji: "✉️", title: "Message" },
  structured_output: { emoji: "🧾", title: "Structured output" },
  session_status: { emoji: "📊", title: "Chat status" },
  github_publish: { emoji: "🔀", title: "Published on GitHub", detailKeys: ["title"] },
  github_identity_status: { emoji: "🔐", title: "GitHub identity" },
};

const FALLBACK: ToolDisplaySpec = { emoji: "🧩", title: "Tool", detailKeys: FALLBACK_DETAIL_KEYS };

/** Reads a possibly dotted key path (`questions.0.question`) out of tool args. */
function readPath(source: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((value, segment) => {
    if (value && typeof value === "object") {
      return (value as Record<string, unknown>)[segment];
    }
    return undefined;
  }, source);
}

function stringifyDetail(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value.trim() || undefined;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const joined = value.filter((item) => typeof item === "string").join(", ");
    return joined || undefined;
  }
  return undefined;
}

/** Strips an MCP/plugin namespace so `mcp__github__list_issues` reads as a tool name. */
function normalizeToolName(name: string): string {
  const trimmed = name.trim();
  const withoutNamespace = trimmed.includes("__")
    ? (trimmed.split("__").pop() ?? trimmed)
    : trimmed;
  return withoutNamespace.toLowerCase();
}

function titleCaseFallback(name: string): string {
  return name
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export type ToolPresentation = {
  emoji: string;
  title: string;
  detail?: string;
};

export function describeTool(name: string, args: unknown): ToolPresentation {
  const normalized = normalizeToolName(name);
  const spec = TOOL_DISPLAY[normalized];
  const resolved = spec ?? { ...FALLBACK, title: titleCaseFallback(name) || FALLBACK.title };
  const detailKeys = resolved.detailKeys ?? FALLBACK_DETAIL_KEYS;

  let detail: string | undefined;
  if (args && typeof args === "object") {
    for (const key of detailKeys) {
      detail = stringifyDetail(readPath(args, key));
      if (detail) {
        break;
      }
    }
  } else {
    detail = stringifyDetail(args);
  }

  return {
    emoji: resolved.emoji,
    title: resolved.title,
    ...(detail ? { detail: detail.replace(/\s+/g, " ").slice(0, 200) } : {}),
  };
}
